import React from 'react';
import { Button, Icon, Field, TextInput, SelectInput, Switch, ConfirmModal } from '../../../components/ui';
import EntityEditor from '../EntityEditor';
import { postJson, del, getJson, withOrig, isDraftDirty } from '../shared';

/*
 * PlaceEditor — the Overview/edit form for a PLACE node in the unified Setup tree
 * (subscriber · customer · site · gate). Extracted verbatim (CRUD-wise) from the old
 * HierarchyPane so the exact endpoints/bodies are preserved:
 *   subscriber  save POST api/gatesubscriber            delete DELETE api/gatesubscriberdestroy/:id   (assoc api/gatesubscriberassociations/:id)
 *   customer    save POST api/gatecustomerNew/          delete DELETE api/gatecustomerdestroyNew/:id  (assoc api/gatecustomerassociations/:id)
 *   site        save POST api/gatesiteNew/              delete DELETE api/gatesitedestroy/:id         (assoc api/gatesiteassociations/:id)
 *   gate        save POST api/gateNew                   delete DELETE api/gatedestroyNew/:id          (assoc api/gateassociations/:id ; clear onsite DELETE api/gateonsiteerase/gate/:id)
 *
 * Contextual creation: in the unified pane you always add a child FROM its parent
 * (the tree "+", or a parent tab's "+ Add"), so the parent is known — it's shown as
 * read-only context, not a picker (the old parent dropdowns are gone). Fixes carried
 * from the panes: Save is gated on a real value-diff (isDraftDirty) and the DB's 1/0
 * flags are coerced to booleans so a flipped-then-restored toggle reads clean.
 *
 * Props: kind, row (null on create), newCtx {subscriberId,customerId,customerName,
 * siteId,siteName,direct}, authId, webToken, sites, toast, and callbacks
 * onSaved({kind,id,name,isNew}), onDeleted({kind,row}), onCancelNew(), onDirtyChange(bool).
 */
const identityOf = (kind, row) => `${kind}:${row ? row.id : 'new'}`;

export default class PlaceEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = { draft: null, saving: false, deleting: false, confirm: null };
    this._lastDirty = false;
  }

  componentDidMount() { this.buildDraft(); }

  componentDidUpdate(prevProps) {
    if (identityOf(prevProps.kind, prevProps.row) !== identityOf(this.props.kind, this.props.row)) {
      this.buildDraft();
    }
    const d = this.isDirty();
    if (d !== this._lastDirty) { this._lastDirty = d; if (this.props.onDirtyChange) this.props.onDirtyChange(d); }
  }

  componentWillUnmount() { if (this._lastDirty && this.props.onDirtyChange) this.props.onDirtyChange(false); }

  isNew = () => !this.props.row;
  isDirty = () => isDraftDirty(this.state.draft);

  // ---- draft assembly (from the row on edit, or newCtx on create) ----
  buildDraft = () => {
    const { kind, row, newCtx = {} } = this.props;
    let draft;
    if (kind === 'subscriber') {
      draft = row
        ? { subscriberId: row.id, name: row.sName, license: row.bLicense, accountType: row.accountType || 'agency' }
        : { subscriberId: 0, name: '', license: 0, accountType: 'agency' };
    } else if (kind === 'customer') {
      draft = row
        ? { customerId: row.id, name: row.sName, subscriberId: row.GateSubscriberId }
        : { customerId: 0, name: '', subscriberId: newCtx.subscriberId || 0 };
    } else if (kind === 'site') {
      draft = row
        ? { siteId: row.id, name: row.sName, customerId: row.GateCustomerId, useNames: !!row.fUseNames, syncOnSubmit: !!row.fSyncOnSubmit }
        : { siteId: 0, name: '', customerId: newCtx.customerId || 0, subscriberId: newCtx.subscriberId || 0, useNames: false, syncOnSubmit: true, direct: !!newCtx.direct };
    } else { // gate
      draft = row
        ? {
            gateId: row.id, name: row.sName,
            active: !!row.fActive, requirePhotos: !!row.fRequirePhotos,
            subscriberId: row.GateSubscriber ? row.GateSubscriber.id : row.GateSubscriberId,
            customerId: row.GateCustomer ? row.GateCustomer.id : row.GateCustomerId,
            customerName: row.GateCustomer ? row.GateCustomer.sName : (newCtx.customerName || ''),
            siteId: row.GateSite ? row.GateSite.id : row.GateSiteId,
            siteName: row.GateSite ? row.GateSite.sName : (newCtx.siteName || ''),
            peopleCount: row.bPeopleCount, vehicleCount: row.bVehicleCount,
          }
        : {
            gateId: 0, name: '', active: false, requirePhotos: true,
            subscriberId: newCtx.subscriberId || 0, customerId: newCtx.customerId || 0,
            customerName: newCtx.customerName || '', siteId: newCtx.siteId || 0, siteName: newCtx.siteName || '',
            peopleCount: 0, vehicleCount: 0,
          };
    }
    this._lastDirty = false;
    this.setState({ draft: withOrig(draft), saving: false, deleting: false, confirm: null });
    if (this.props.onDirtyChange) this.props.onDirtyChange(false);
  };

  setDraft = (patch) => this.setState((st) => ({ draft: { ...st.draft, ...patch } }));

  // ---- save (verbatim endpoints/bodies) ----
  save = async () => {
    const { kind } = this.props;
    const d = this.state.draft;
    this.setState({ saving: true });
    try {
      let res;
      if (kind === 'subscriber') {
        res = await postJson('api/gatesubscriber', { id: d.subscriberId, sName: (d.name || '').trim(), bLicense: parseInt(d.license, 10) || 0, accountType: d.accountType || 'agency' }, this.props.webToken);
      } else if (kind === 'customer') {
        res = await postJson('api/gatecustomerNew/', { id: d.customerId, sName: (d.name || '').trim(), GateSubscriberId: d.subscriberId }, this.props.webToken);
      } else if (kind === 'site') {
        res = await postJson('api/gatesiteNew/', { id: d.siteId, sName: (d.name || '').trim(), GateCustomerId: d.customerId, fUseNames: d.useNames, fSyncOnSubmit: d.syncOnSubmit }, this.props.webToken);
      } else {
        res = await postJson('api/gateNew', { id: d.gateId, sName: (d.name || '').trim(), GateSubscriberId: d.subscriberId, GateCustomerId: d.customerId, GateSiteId: d.siteId, fActive: d.active, fRequirePhotos: d.requirePhotos }, this.props.webToken);
      }
      this.setState({ saving: false });
      if (res && res.error) {
        if (kind === 'gate' && res.error === 'fActive') {
          this.props.toast.error('Error: Active gates cannot exceed authorized subscriber license.');
          this.setState((st) => ({ draft: { ...st.draft, active: (this.props.row && !!this.props.row.fActive) || false } }));
        } else {
          this.props.toast.error('Error: ' + res.error);
        }
        return;
      }
      const isNew = this.isNew();
      const label = kind[0].toUpperCase() + kind.slice(1);
      this.props.toast.success(`${label} ${isNew ? 'add' : 'edit'} successful`);
      if (!isNew) {
        // Re-baseline the draft to the just-saved values so the editor reads CLEAN after a successful
        // edit — otherwise _orig keeps the pre-save snapshot, Save stays enabled, and navigating away
        // pops a spurious "discard unsaved changes?" (the editor stays mounted for place edits).
        this._lastDirty = false;
        this.setState((st) => ({ draft: withOrig({ ...st.draft }) }));
        if (this.props.onDirtyChange) this.props.onDirtyChange(false);
      }
      if (this.props.onSaved) this.props.onSaved({ kind, id: res && res.id, name: (d.name || '').trim(), isNew });
    } catch (e) {
      console.error('Error:', e);
      this.setState({ saving: false });
      this.props.toast.error(`${this.props.kind[0].toUpperCase() + this.props.kind.slice(1)} save failed. If the problem continues please contact your installer.`);
    }
  };

  clearOnsite = async () => {
    if (!window.confirm('This will delete all people and vehicles currently listed onsite. Are you sure you wish to continue?')) return;
    try {
      await del('api/gateonsiteerase/gate/' + this.state.draft.gateId, this.props.webToken);
      this.setState((st) => ({ draft: { ...st.draft, peopleCount: 0, vehicleCount: 0 } }));
      this.props.toast.success('Onsite list cleared for this gate');
    } catch (e) {
      this.props.toast.error('Error: Unable to clear onsite list for this gate.');
    }
  };

  // ---- delete (assoc-check → confirm → destroy) ----
  askDelete = async () => {
    const { kind, row } = this.props;
    if (!row) return;
    const assocPath = { subscriber: 'api/gatesubscriberassociations/', customer: 'api/gatecustomerassociations/', site: 'api/gatesiteassociations/', gate: 'api/gateassociations/' }[kind];
    let assoc = null;
    try { assoc = await getJson(assocPath + row.id, this.props.webToken); } catch (e) { /* originals swallow + still prompt */ }
    this.setState({ confirm: { assoc } });
  };
  doDelete = async () => {
    const { kind, row } = this.props;
    const path = { subscriber: 'api/gatesubscriberdestroy/', customer: 'api/gatecustomerdestroyNew/', site: 'api/gatesitedestroy/', gate: 'api/gatedestroyNew/' }[kind];
    this.setState({ deleting: true });
    try {
      await del(path + row.id, this.props.webToken);
      this.setState({ deleting: false, confirm: null });
      this.props.toast.success(`${kind[0].toUpperCase() + kind.slice(1)} delete successful`);
      if (this.props.onDeleted) this.props.onDeleted({ kind, row });
    } catch (e) {
      console.log(`${kind} delete failed`, e);
      this.setState({ deleting: false, confirm: null });
      this.props.toast.error(`${kind[0].toUpperCase() + kind.slice(1)} delete failed. If the problem continues please contact your installer.`);
    }
  };

  onCancel = () => {
    if (this.isNew()) { if (this.props.onCancelNew) this.props.onCancelNew(); }
    else this.buildDraft(); // revert edits to the original row
  };

  render() {
    const { kind } = this.props;
    const { draft, saving, deleting } = this.state;
    if (!draft) return null;
    const isNew = this.isNew();
    const canSaveBase = !!(draft.name && draft.name.trim());
    const canSave = (kind === 'gate' ? canSaveBase && !!draft.siteId : canSaveBase) && this.isDirty();
    const label = { subscriber: 'Subscriber', customer: 'Customer', site: 'Site', gate: 'Gate' }[kind];

    return (
      <>
        <EntityEditor
          chromeless
          title={isNew ? `New ${label}` : draft.name || label}
          statusPill={kind === 'gate' && !isNew ? { label: draft.active ? 'Active' : 'Inactive', variant: draft.active ? 'in' : 'auth' } : undefined}
          onSubmit={this.save}
          onCancel={this.onCancel}
          onDelete={isNew ? undefined : this.askDelete}
          saving={saving}
          deleting={deleting}
          saveLabel={isNew ? `Create ${kind}` : 'Save changes'}
          canSave={canSave}
        >
          {this.renderFields()}
        </EntityEditor>
        {this.state.confirm && this.renderConfirm()}
      </>
    );
  }

  renderFields() {
    const { kind } = this.props;
    const { draft } = this.state;
    if (kind === 'subscriber') {
      return (
        <div className="formgrid">
          <Field label="Subscriber name" full required>
            <TextInput value={draft.name} placeholder="e.g. S&amp;S Gate" onChange={(e) => this.setDraft({ name: e.target.value })} />
          </Field>
          <Field label="Account type" required>
            <SelectInput value={draft.accountType} onChange={(e) => this.setDraft({ accountType: e.target.value })}>
              <option value="agency">Agency — manages its own customers</option>
              <option value="direct">Direct — is its own customer (sites &amp; gates only)</option>
            </SelectInput>
          </Field>
          <Field label="License (max active gates)" required>
            <TextInput type="number" min="0" value={draft.license} placeholder="0" onChange={(e) => this.setDraft({ license: e.target.value })} />
          </Field>
        </div>
      );
    }
    if (kind === 'customer') {
      return (
        <div className="formgrid">
          <Field label="Customer name" full required>
            <TextInput value={draft.name} placeholder="enter name" onChange={(e) => this.setDraft({ name: e.target.value })} />
          </Field>
        </div>
      );
    }
    if (kind === 'site') {
      return (
        <>
          <div className="formgrid">
            <Field label="Site name" full required>
              <TextInput value={draft.name} placeholder="enter name" onChange={(e) => this.setDraft({ name: e.target.value })} />
            </Field>
          </div>
          <div className="section-label"><span className="eyebrow">Site behavior</span></div>
          <div className="toggles">
            <label className="togglecard">
              <div className="grow"><strong>Passenger names</strong><small>Collect names instead of a head-count</small></div>
              <Switch checked={!!draft.useNames} onChange={(v) => this.setDraft({ useNames: v })} label="Use passenger names" />
            </label>
            <label className="togglecard">
              <div className="grow"><strong>Sync on submit</strong><small>Upload immediately vs. batch on timer</small></div>
              <Switch checked={!!draft.syncOnSubmit} onChange={(v) => this.setDraft({ syncOnSubmit: v })} label="Sync events on submit" />
            </label>
          </div>
        </>
      );
    }
    // gate
    const custName = draft.customerName && draft.customerName !== 'Customer N/A' ? draft.customerName : '';
    return (
      <>
        <div className="formgrid">
          <Field label="Gate name" full required>
            <TextInput value={draft.name} placeholder="e.g. North Entrance" onChange={(e) => this.setDraft({ name: e.target.value })} />
          </Field>
          <Field label="Site">
            <span className={`readonly-val${draft.siteName ? '' : ' is-empty'}`}>{draft.siteName || '—'}</span>
          </Field>
          <Field label="Customer">
            <span className={`readonly-val${custName ? '' : ' is-empty'}`}>{custName || '—'}</span>
          </Field>
        </div>
        <div className="section-label"><span className="eyebrow">Gate behavior</span></div>
        <p className="sub" style={{ margin: '-4px 0 14px' }}>These flags change what guards see and must do on the mobile app at this gate.</p>
        <div className="toggles">
          <label className="togglecard">
            <div className="grow"><strong>Active</strong><small>Guards can log crossings here</small></div>
            <Switch checked={!!draft.active} onChange={(v) => this.setDraft({ active: v })} label="Active" />
          </label>
          <label className="togglecard">
            <div className="grow"><strong>Require photos</strong><small>Force LPN + load photos before submit</small></div>
            <Switch checked={!!draft.requirePhotos} onChange={(v) => this.setDraft({ requirePhotos: v })} label="Require photos" />
          </label>
        </div>
        {!this.isNew() && (
          <>
            <div className="section-label"><span className="eyebrow">On-site now</span></div>
            <div className="countbox">
              <div className="countbox__stats">
                <div className="countbox__stat"><span className="num">{draft.peopleCount}</span><small>People</small></div>
                <div className="countbox__stat"><span className="num">{draft.vehicleCount}</span><small>Vehicles</small></div>
              </div>
              {(draft.peopleCount > 0 || draft.vehicleCount > 0) && (
                <Button size="sm" variant="default" onClick={this.clearOnsite}>Clear onsite list</Button>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  renderConfirm() {
    const { kind, row } = this.props;
    const assoc = this.state.confirm.assoc;
    const hasAssoc = assoc && assoc.total > 0;
    return (
      <ConfirmModal
        open
        onClose={() => this.setState({ confirm: null })}
        onConfirm={this.doDelete}
        onCancel={() => this.setState({ confirm: null })}
        title={`Delete this ${kind}?`}
        confirmLabel="Delete"
        danger
        loading={this.state.deleting}
      >
        <p className="ui-modal__msg" style={{ marginBottom: 8 }}>
          This action cannot be undone. <strong>{row.sName}</strong>
        </p>
        {hasAssoc && (
          <div className="devnote" style={{ background: 'var(--ev-accident-bg)', borderColor: 'var(--ev-accident-bd)', color: 'var(--ev-accident)' }}>
            <Icon name="warning" size={16} />
            <span>
              Associated records will also be deleted
              {assoc.customers ? ` · Customers: ${assoc.customers}` : ''}
              {assoc.sites ? ` · Sites: ${assoc.sites}` : ''}
              {assoc.gates ? ` · Gates: ${assoc.gates}` : ''}
              {assoc.assignments ? ` · Assignments: ${assoc.assignments}` : ''}
              {assoc.users ? ` · Users: ${assoc.users}` : ''}
              {assoc.companies ? ` · Companies: ${assoc.companies}` : ''}
              {assoc.lpns ? ` · LPNs: ${assoc.lpns}` : ''}
              {assoc.people ? ` · People: ${assoc.people}` : ''}
            </span>
          </div>
        )}
      </ConfirmModal>
    );
  }
}
